import React, { useState, useEffect } from 'react';
import Select, { components } from 'react-select';
import { withNamespaces } from 'react-i18next';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { isArray, uniqueId } from 'lodash';
import { ErrorMessage } from 'formik';
import RoundedTitle from '../RoundedTitle';
import { customStyles, DropdownIndicator } from '../../Select/Common';
import LabelR from '../../Simple/LabelR';

const SelectFormik = (props) => {
  const {
    field: { name, value = '' },
    form: { setFieldValue, setFieldTouched, errors, touched },
    label,
    isDisabled = false,
    required,
    requiredFields = [name],
    options,
    placeholder,
    customValueContainer,
    contentAddon,
    minCharactersToRender,
    dropDownAddon,
    className,
    t,
    addon,
    customDropdownIcon,
    isMultiple,
    isClearable,
    maxMenuHeight,
    closeMenuOnSelect,
    selectFirstValue,
    returnFullValue,
    handleBlur,
    handleChange,
    handleKeyDown,
    showError,
    haveIconOnText = false,
    valueFromOutSideInput = '',
  } = props;

  const classname = classNames(className, { 'is-invalid': (errors[name] && touched[name]) || showError });
  const [inputCharsLength, setInputCharsLength] = useState((value && value.length) || 0);

  useEffect(() => {
    if (selectFirstValue && value === '' && options.length) onChange(options[0]);
  });

  useEffect(() => {
    if (valueFromOutSideInput.length > 0) setFieldValue(name, valueFromOutSideInput);
  }, [valueFromOutSideInput]);

  const handleAffectedFields = (affectedFields) => {
    if (affectedFields) {
      Object.keys(affectedFields).forEach((key) => setFieldValue(key, affectedFields[key]));
    }
  };

  const valueContainer = ({ children, ...props }) => {
    const { getValue, hasValue } = props;
    const nbValues = getValue().length;
    if (!hasValue) {
      return <components.ValueContainer {...props}>{children}</components.ValueContainer>;
    }
    return <components.ValueContainer {...props}>{`${nbValues} items selected`}</components.ValueContainer>;
  };

  const multiValueContainer = ({ children, ...props }) => {
    return <components.MultiValueContainer {...props} />;
  };

  const option = (option) => {
    return components.Option && <components.Option {...option}>{text(option)}</components.Option>;
  };

  const text = (item, innerProps) => {
    return isMultiple ? (
      <div id={`check${item.value}`} className="options-check custom-control custom-checkbox" {...innerProps}>
        <input type="checkbox" className="custom-control-input" id={`check${item.value}`} checked={item.isSelected} onChange={() => null} />
        <span id={`value${item.value}`} className="custom-label custom-control-label">
          {item.label}
        </span>
        {dropDownAddon && dropDownAddon(item)}
      </div>
    ) : (
      <div id={`check${item.value}`} className="options-check w-100" {...innerProps}>
        <span id={`check${item.value}`} className="custom-label">
          {item.label}
        </span>
        {addon && addon(item)}
      </div>
    );
  };

  const noOptionsMessage = () => {
    if ((minCharactersToRender && minCharactersToRender <= inputCharsLength) || !minCharactersToRender) {
      return t('noOptionsMessage');
    }
    return t('labelStartTyping');
  };

  const onInputChange = (newValues) => {
    setInputCharsLength(newValues.length);
  };

  const onChange = (newValues = '') => {
    const mapValues = isArray(newValues) ? newValues.map((o) => o.value) : '';
    const arrayValues = newValues && newValues.label ? newValues.value : mapValues;
    const finalValues = returnFullValue ? newValues : arrayValues;
    setFieldValue(name, finalValues);
    handleChange(finalValues, handleAffectedFields);
  };

  const onBlur = () => {
    setFieldTouched(name, true);
    handleBlur();
  };

  const getSelectOptions = () => {
    let ret = !contentAddon ? options.map((o) => ({ ...o, label: haveIconOnText ? o.label : t(o.label) })) : options;
    const valueSelected = options.filter((o) => o.value === value);

    if (isMultiple) {
      if (minCharactersToRender) {
        ret = minCharactersToRender <= inputCharsLength ? ret : [];
      }
    } else if (value !== '' && !isNaN(value) && valueSelected.length === 0) {
      handleChange(null, handleAffectedFields); // options changed and current value do not exist -> remove value
    } else if (minCharactersToRender) {
      if (value && valueSelected.length > 0) {
        // There is an item selected
        ret = valueSelected;
      } else {
        const hasSufficientChars = minCharactersToRender <= inputCharsLength;
        ret = hasSufficientChars ? ret : [];
      }
    }
    return ret;
  };

  const customComponents = isMultiple
    ? {
        DropdownIndicator,
        Option: option,
        ...(customValueContainer && { ValueContainer: valueContainer }),
        ...(!customValueContainer && {
          MultiValueContainer: multiValueContainer,
        }),
      }
    : {
        ...(customDropdownIcon && { DropdownIndicator }),
        Option: option,
      };

  const onlyValuesFromArray = () => {
    const v = value || [];
    const isPairLabelValue = v[0] && v[0].label;
    return returnFullValue && isPairLabelValue ? v.reduce((ret, item) => [...ret, item.value], []) : v;
  };

  const optionsTranslated = options.map(({ label, value }) => ({
    value,
    label: haveIconOnText ? label : t(label),
  }));
  const values = isMultiple ? optionsTranslated.filter((o) => onlyValuesFromArray().includes(o.value)) : optionsTranslated.find((o) => value === o.value);

  const handleOptionClose = (option) => {
    onChange(
      values.filter((item) => {
        return item.value !== (option.value ? option.value : option);
      })
    );
  };

  return (
    <div className="form-group" id={`${name}-container-simple-select`}>
      {label &&
        (required ? (
          <LabelR requiredFields={requiredFields} isEditable={!isDisabled} htmlFor={name} name={name}>
            {label}
          </LabelR>
        ) : (
          <label htmlFor={name} id={`${name}-label`}>
            {label}
          </label>
        ))}
      <Select
        inputId={`${name}-simple-select`}
        hideSelectedOptions={false}
        backspaceRemovesValue={false}
        isMulti={isMultiple}
        components={customComponents}
        name={name}
        options={getSelectOptions()}
        styles={customStyles}
        placeholder={placeholder}
        noOptionsMessage={noOptionsMessage}
        onInputChange={onInputChange}
        onChange={onChange}
        onBlur={onBlur}
        value={values || null}
        className={classname}
        isDisabled={isDisabled}
        onKeyDown={handleKeyDown}
        maxMenuHeight={maxMenuHeight}
        closeMenuOnSelect={closeMenuOnSelect}
        {...(!isMultiple && {
          autoBlur: true,
          isClearable,
        })}
      />
      {isMultiple && values.length > 0 && (
        <div id="selected-options" className="selected-options">
          {values.map(
            (item) =>
              (contentAddon && contentAddon(item, handleOptionClose)) || (
                <RoundedTitle key={uniqueId()} title={item.label} className="rounded-title-darker options" withClose={!isDisabled} onClose={() => handleOptionClose(item)} />
              )
          )}
        </div>
      )}
      {touched[name] && errors[name] && <ErrorMessage name={name} component="div" className="invalid-feedback" />}
    </div>
  );
};

SelectFormik.defaultProps = {
  customValueContainer: true,
  isMultiple: false,
  isClearable: true,
  maxMenuHeight: 240,
  closeMenuOnSelect: true,
  selectFirstValue: false,
  returnFullValue: false,
  handleBlur: () => {},
  handleChange: () => {},
  handleKeyDown: () => {},
};

SelectFormik.propTypes = {
  options: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  placeholder: PropTypes.string,
  handleChange: PropTypes.func,
  handleBlur: PropTypes.func,
  value: PropTypes.arrayOf(PropTypes.shape({})),
  dropDownAddon: PropTypes.func,
  contentAddon: PropTypes.func,
  customValueContainer: PropTypes.bool,
  minCharactersToRender: PropTypes.number,
  customDropdownIcon: PropTypes.bool,
  isDisabled: PropTypes.bool,
  onKeyDown: PropTypes.func,
  maxMenuHeight: PropTypes.number,
  returnFullValue: PropTypes.bool,
  isClearable: PropTypes.bool,
  isMultiple: PropTypes.bool,
  closeMenuOnSelect: PropTypes.bool,
  selectFirstValue: PropTypes.bool,
  handleKeyDown: PropTypes.func,
  label: PropTypes.string,
  required: PropTypes.bool,
  className: PropTypes.string,
  t: PropTypes.func,
  addon: PropTypes.func,
};

export default withNamespaces()(SelectFormik);
